import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { Field, TextInput, TextArea, SelectInput, ImageUpload, AdminModal, StatusPill } from "@/admin/adminUi";

const CATS = ["ORAYA", "AI", "Systems", "Industry", "Technology", "Research"];
const EMPTY = { title: "", slug: "", description: "", author: "ORAYA", category: "ORAYA", cover_image: "", content: "", seo_title: "", seo_description: "", status: "draft" };

export default function AdminInsights() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/insights").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/insights/${editing.id}`, editing);
      else await api.post("/admin/insights", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this article?")) return; await api.delete(`/admin/insights/${id}`); toast.success("Deleted"); load(); };

  const togglePublish = async (it) => {
    const next = it.status === "published" ? "draft" : "published";
    try {
      await api.put(`/admin/insights/${it.id}`, { ...it, status: next });
      toast.success(next === "published" ? "Published" : "Moved to draft");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="display text-3xl font-light text-oraya-mist">Insights</h1><p className="text-oraya-slate mt-1">Articles and perspectives.</p></div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-insight" className="inline-flex items-center gap-2 rounded-lg bg-oraya-mist text-oraya-black px-4 py-2.5 text-sm font-medium hover:bg-white"><Plus className="w-4 h-4" /> New article</button>
      </div>

      <div className="bg-oraya-graphite border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-oraya-slate border-b border-white/[0.06]">
            <th className="px-6 py-4 font-medium">Title</th><th className="px-6 py-4 font-medium hidden md:table-cell">Category</th>
            <th className="px-6 py-4 font-medium">Status</th><th className="px-6 py-4 font-medium text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-white/[0.04] text-oraya-mist" data-testid={`insight-row-${it.slug}`}>
                <td className="px-6 py-4 font-medium">{it.title}</td>
                <td className="px-6 py-4 hidden md:table-cell text-oraya-slate">{it.category}</td>
                <td className="px-6 py-4"><StatusPill status={it.status} /></td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => togglePublish(it)} data-testid={`toggle-insight-${it.slug}`} className="text-xs text-oraya-slate hover:text-oraya-mist px-2 py-1 rounded-md border border-white/10 mr-1">
                    {it.status === "published" ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => setEditing({ ...it })} data-testid={`edit-insight-${it.slug}`} className="text-oraya-slate hover:text-oraya-mist p-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(it.id)} data-testid={`delete-insight-${it.slug}`} className="text-oraya-slate hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-oraya-slate">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <AdminModal title={editing.id ? "Edit article" : "New article"} onClose={() => setEditing(null)} onSave={save} saving={saving} testid="insight-modal">
          <Field label="Title"><TextInput value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="insight-title" /></Field>
          <Field label="Slug (optional)"><TextInput value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} placeholder="auto from title" testid="insight-slug" /></Field>
          <Field label="Description"><TextArea rows={2} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="insight-description" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Author"><TextInput value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="insight-author" /></Field>
            <Field label="Category"><SelectInput value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} testid="insight-category" options={CATS.map((c) => ({ value: c, label: c }))} /></Field>
          </div>
          <Field label="Content"><TextArea rows={8} value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} testid="insight-content" /></Field>
          <Field label="SEO title"><TextInput value={editing.seo_title} onChange={(v) => setEditing({ ...editing, seo_title: v })} testid="insight-seo-title" /></Field>
          <Field label="SEO description"><TextArea rows={2} value={editing.seo_description} onChange={(v) => setEditing({ ...editing, seo_description: v })} testid="insight-seo-description" /></Field>
          <Field label="Status"><SelectInput value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} testid="insight-status" options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} /></Field>
          <Field label="Cover image"><ImageUpload value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="insight-cover" /></Field>
        </AdminModal>
      )}
    </div>
  );
}
